import { Button, Checkbox, Grid, Text } from '@mantine/core';
import { useTranslation } from 'react-i18next';
import { IOrder } from '../../interfaces/Order';
import LoaderOverlay from '../../components/LoaderOverlay/LoaderOverlay';
import PaymentConfigEnum, { MercadoPagoEnum } from '../../enum/PaymentConfig';
import { useGetCashierboxes } from '../../hooks/cashierboxes/queries';
import { useForm } from '@mantine/form';
import { requiredFieldFormValidator } from '../../utils/validators/formValidators';
import { memo, useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { ICashierBox } from '../../interfaces/Store';
import PaymentCaptureFields from '../Payments/PaymentCaptureFields';
import { PaymentMethodsEnum } from '../../enum/PaymentMethodsEnum';
import {
  calculateCashChange,
  calculateRemainingBalance
} from '../../utils/payments/paymentAmounts';
import { showNotification } from '../../utils/Notification/Notification';

export default memo(function AmountDuePaymentModal({
  order,
  storeId,
  onSubmit,
  onMercadoPagoSelected,
  allowMercadoPago,
  amountDue,
  close,
  pinUser
}: {
  order: IOrder;
  storeId: string;
  onSubmit: ({
    orderId,
    sequence,
    storeId,
    paymentMethod,
    cashierBoxId,
    amount,
    paymentDate,
    collectWithAmountDue,
    pinUserId,
    attemptId
  }: {
    orderId?: string;
    sequence: string;
    storeId: string;
    paymentMethod: string;
    cashierBoxId: string;
    amount: number;
    paymentDate: string;
    collectWithAmountDue: boolean;
    pinUserId?: string;
    pinValidationToken?: string;
    attemptId?: string | null;
  }) => void;
  onMercadoPagoSelected?: ({
    order,
    orderId,
    storeId,
    paymentMethod,
    cashierBoxId,
    amount,
    paymentDate,
    collectWithAmountDue,
    pinUserId
  }: {
    order: IOrder;
    orderId?: string;
    storeId: string;
    paymentMethod: string;
    cashierBoxId: string;
    amount: number;
    paymentDate: string;
    collectWithAmountDue: boolean;
    pinUserId?: string;
    pinValidationToken?: string;
  }) => void;
  allowMercadoPago?: boolean;
  amountDue: number;
  close: () => void;
  pinUser: {
    _id: string;
    name: string;
    cashierBox: string;
    permission: any;
    pinValidationToken?: string;
  } | null;
}) {
  const { t } = useTranslation(['FieldName', 'CommonButtons', 'Backend', 'Notifications']);
  const [isUpdatingOrder, setIsUpdatingOrder] = useState(false);
  const [cashReceivedAmount, setCashReceivedAmount] = useState<number | ''>('');
  const { isLoading: isGetCashierboxesLoading, data: cashierboxes } = useGetCashierboxes(
    !!storeId,
    storeId
  );
  const { user } = useSelector((state: any) => state.user);

  const getInitialCashierBox = (cashierboxes: ICashierBox[] | undefined) => {
    if (!cashierboxes) return '';
    if (cashierboxes.length === 0) return '';
    if (pinUser?.cashierBox) {
      //GET THE CASHIERBOX FROM THE PIN USER
      const cashierBox = cashierboxes.find((cashierbox) => cashierbox._id === pinUser.cashierBox);
      //if no cashierbox found, return the first one
      return cashierBox?._id || cashierboxes[0]?._id;
    }
    //USUALLY STAFF USER FALLS TO THIS
    if (typeof user.cashierBox === 'string' && user.cashierBox) {
      // WE FIND THE CASHIERBOX FROM THE USER IN CASE IS IN ANOTHER STORE AND CASHIERBOX ASSIGNED IS FROM OTHER STORE
      const cashierBox = cashierboxes.find((cashierbox) => cashierbox._id === user.cashierBox);
      return cashierBox?._id || (cashierboxes || [])[0]?._id;
    }
    //USUALLY COMPANY ADMIN FALLS TO THIS
    return (cashierboxes || [])[0]?._id;
  };

  type FormValues = {
    amountPaid: number;
    paymentMethod: string;
    cashierBox: string;
    collectWithAmountDue: boolean;
  };

  const form = useForm<FormValues>({
    mode: 'controlled',
    initialValues: {
      amountPaid: amountDue,
      paymentMethod: PaymentMethodsEnum.Card,
      cashierBox: getInitialCashierBox(cashierboxes) || '',
      collectWithAmountDue: false
    },
    validate: {
      amountPaid: (value: number, values) => {
        const requiredValidatorRes = requiredFieldFormValidator(value);
        if (requiredValidatorRes) return requiredValidatorRes;
        const numericValue = Number(value || 0);
        if (!values.collectWithAmountDue && numericValue <= 0) {
          return 'El monto a aplicar debe ser mayor a 0.';
        }
        if (numericValue > amountDue) {
          return 'El monto a aplicar no puede ser mayor al total pendiente.';
        }
        return null;
      },
      paymentMethod: (value: string) => {
        const requiredValidatorRes = requiredFieldFormValidator(value);
        if (requiredValidatorRes) return requiredValidatorRes;
        return null;
      },
      cashierBox: (value: string) => {
        const requiredValidatorRes = requiredFieldFormValidator(value);
        if (requiredValidatorRes) return requiredValidatorRes;
        return null;
      }
    }
  });

  const paymentOptions = useMemo(() => {
    const methods = [PaymentConfigEnum.CARD, PaymentConfigEnum.CASH, PaymentConfigEnum.TRANSFER];
    const baseOptions = methods.map((method) => ({
      value: method,
      label: t(`FieldName:paymentMethods.${method}`)
    }));
    if (allowMercadoPago) {
      baseOptions.push({
        value: MercadoPagoEnum.MERCADOPAGO,
        label: t(`FieldName:paymentMethods.${MercadoPagoEnum.MERCADOPAGO}`)
      });
    }
    return baseOptions;
  }, [allowMercadoPago, t]);

  const amountToApply = Number(form.values.amountPaid || 0);
  const isCashPayment = form.values.paymentMethod === PaymentMethodsEnum.Cash;
  const remainingBalance = calculateRemainingBalance(amountDue, amountToApply);
  const changeAmount = calculateCashChange(amountToApply, cashReceivedAmount);

  form.watch('collectWithAmountDue', ({ value }: { value: boolean }) => {
    if (value) {
      form.setFieldValue('amountPaid', 0);
    } else {
      form.setFieldValue('amountPaid', amountDue);
    }
  });

  const handleSubmit = async (values: FormValues) => {
    try {
      if (values.paymentMethod === PaymentMethodsEnum.Cash) {
        const receivedAmount = Number(cashReceivedAmount || 0);
        const hasCashReceivedAmount = cashReceivedAmount !== '';

        if (hasCashReceivedAmount && receivedAmount < Number(values.amountPaid || 0)) {
          showNotification({
            type: 'Error',
            title: t('Notifications:somethingWrong'),
            message: 'El efectivo recibido debe ser mayor o igual al monto a aplicar.'
          });
          return;
        }
      }

      setIsUpdatingOrder(true);
      const payload = {
        orderId: order._id || '',
        sequence: order.sequence || '',
        collectWithAmountDue: values.collectWithAmountDue,
        paymentMethod: values.paymentMethod,
        cashierBoxId: values.cashierBox || '',
        amount: values.amountPaid,
        paymentDate: new Date().toISOString(),
        pinUserId: pinUser?._id,
        ...(pinUser?.pinValidationToken ? { pinValidationToken: pinUser.pinValidationToken } : {}),
        storeId: storeId,
        attemptId: null
      };
      if (values.paymentMethod === MercadoPagoEnum.MERCADOPAGO && allowMercadoPago) {
        onMercadoPagoSelected?.({
          ...payload,
          order
        });
      } else {
        await onSubmit(payload);
      }
      close();
    } catch (e) {
      throw e;
    } finally {
      setIsUpdatingOrder(false);
    }
  };

  useEffect(() => {
    if (cashierboxes?.length) {
      const initialCashierBox = getInitialCashierBox(cashierboxes);
      const initialValues = {
        ...form.values,
        amountPaid: amountDue,
        paymentMethod: PaymentMethodsEnum.Card,
        cashierBox: initialCashierBox || ''
      };
      form.setValues(initialValues);
      form.resetDirty(initialValues);
      setCashReceivedAmount('');
    }
  }, [cashierboxes]);

  useEffect(() => {
    if (!isCashPayment) {
      setCashReceivedAmount('');
    }
  }, [isCashPayment]);

  return (
    <>
      <Text>
        Este pedido cuenta con monto pendiente de pago, al actualizar el pedido, el monto pendiente
        a pagar se registrará como un pago y la orden se registrara como entregada
      </Text>
      <Text fw={'bold'}>
        {t('FieldName:amountDue')}: {amountDue}
      </Text>
      <form
        data-testid="amount-due-payment-form"
        onSubmit={form.onSubmit((vals: FormValues) => handleSubmit(vals))}
      >
        <Grid gutter="md">
          <Grid.Col>
            <LoaderOverlay visible={isGetCashierboxesLoading || isUpdatingOrder} />
          </Grid.Col>
          <Grid.Col>
            <Checkbox
              label={t('FieldName:collectWithAmountDue')}
              {...form.getInputProps('collectWithAmountDue', {
                type: 'checkbox'
              })}
            />
          </Grid.Col>
          <PaymentCaptureFields
            cashierboxes={cashierboxes}
            cashierBoxInputProps={form.getInputProps('cashierBox')}
            paymentMethod={form.values.paymentMethod}
            onPaymentMethodChange={(value) => form.setFieldValue('paymentMethod', value)}
            paymentMethodOptions={paymentOptions}
            paymentMethodTestIdPrefix="amount-due-payment-method"
            amountInputProps={form.getInputProps('amountPaid')}
            amountReadOnly
            amountDisabled
            amountDue={amountDue}
            amountToApply={amountToApply}
            remainingBalance={remainingBalance}
            showCashReceived={isCashPayment}
            cashReceivedAmount={cashReceivedAmount}
            onCashReceivedAmountChange={setCashReceivedAmount}
            changeAmount={changeAmount}
          />
          <Grid.Col>
            <Button
              data-testid="amount-due-payment-submit"
              type="submit"
              fullWidth
              loading={isUpdatingOrder}
            >
              {t('CommonButtons:submit')}
            </Button>
          </Grid.Col>
        </Grid>
      </form>
    </>
  );
});
